import * as Schema from "effect/Schema";

import { DEFAULT_MODEL } from "./model.ts";
import { ModelSelection } from "./orchestration.ts";
import { ProviderInstanceId } from "./providerInstance.ts";

export const FeatureFlagKind = Schema.Literals(["capability", "agent"]);
export type FeatureFlagKind = typeof FeatureFlagKind.Type;

interface FeatureFlagEntry<Key extends string, Kind extends FeatureFlagKind, S extends Schema.Top> {
  readonly key: Key;
  /** Key in the flag files and in the `T3CODE_FEATURE_FLAGS` override. */
  readonly fileKey: string;
  readonly kind: Kind;
  readonly schema: S;
  readonly default: S["Type"];
}

const defineFeatureFlag = <
  const Key extends string,
  const Kind extends FeatureFlagKind,
  S extends Schema.Top,
>(
  entry: FeatureFlagEntry<Key, Kind, S>,
): FeatureFlagEntry<Key, Kind, S> => entry;

export const FEATURE_FLAGS = [
  defineFeatureFlag({
    key: "voiceInput",
    fileKey: "voice_input",
    kind: "capability",
    schema: Schema.Boolean,
    default: false,
  }),
  defineFeatureFlag({
    key: "deepWork",
    fileKey: "deep_work",
    kind: "capability",
    schema: Schema.Boolean,
    default: false,
  }),
  defineFeatureFlag({
    key: "model",
    fileKey: "model",
    kind: "agent",
    schema: ModelSelection,
    default: { instanceId: ProviderInstanceId.make("codex"), model: DEFAULT_MODEL },
  }),
] as const;

export type FeatureFlagDefinition = (typeof FEATURE_FLAGS)[number];
type CapabilityDefinition = Extract<FeatureFlagDefinition, { readonly kind: "capability" }>;
type BooleanDefinition = Extract<FeatureFlagDefinition, { readonly schema: Schema.Boolean }>;

export const FeatureFlagKey = Schema.Literals(FEATURE_FLAGS.map((definition) => definition.key));
export type FeatureFlagKey = typeof FeatureFlagKey.Type;
export type FeatureFlagCapabilityKey = CapabilityDefinition["key"];

export const FEATURE_FLAG_CAPABILITIES = FEATURE_FLAGS.filter(
  (definition): definition is CapabilityDefinition => definition.kind === "capability",
);

export const BOOLEAN_FEATURE_FLAGS = FEATURE_FLAGS.filter(
  (definition): definition is BooleanDefinition => definition.schema === Schema.Boolean,
);

type ValueFields<D extends FeatureFlagDefinition> = {
  readonly [E in D as E["key"]]: E["schema"];
};

type PatchFields = {
  readonly [E in FeatureFlagDefinition as E["key"]]: Schema.optionalKey<Schema.NullOr<E["schema"]>>;
};

const valueFields = <D extends FeatureFlagDefinition>(definitions: ReadonlyArray<D>) =>
  Object.fromEntries(
    definitions.map((definition) => [definition.key, definition.schema]),
  ) as ValueFields<D>;

export const FeatureFlagSnapshot = Schema.Struct(valueFields(FEATURE_FLAGS));
export type FeatureFlagSnapshot = typeof FeatureFlagSnapshot.Type;

export const FeatureFlagCapabilities = Schema.Struct(valueFields(FEATURE_FLAG_CAPABILITIES));
export type FeatureFlagCapabilities = typeof FeatureFlagCapabilities.Type;

/** `null` removes the key from the team file, so the image value applies again. */
export const FeatureFlagPatch = Schema.Struct(
  Object.fromEntries(
    FEATURE_FLAGS.map((definition) => [
      definition.key,
      Schema.optionalKey(Schema.NullOr(definition.schema)),
    ]),
  ) as PatchFields,
);
export type FeatureFlagPatch = typeof FeatureFlagPatch.Type;

export const FeatureFlagOrigin = Schema.Literals(["image", "team", "override"]);
export type FeatureFlagOrigin = typeof FeatureFlagOrigin.Type;

export const FeatureFlagOrigins = Schema.Struct(
  Object.fromEntries(FEATURE_FLAGS.map((definition) => [definition.key, FeatureFlagOrigin])) as {
    readonly [K in FeatureFlagKey]: typeof FeatureFlagOrigin;
  },
);
export type FeatureFlagOrigins = typeof FeatureFlagOrigins.Type;

export const FeatureFlagIssue = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("invalid-file"), detail: Schema.String }),
  Schema.Struct({
    kind: Schema.Literal("invalid-value"),
    key: FeatureFlagKey,
    detail: Schema.String,
  }),
]);
export type FeatureFlagIssue = typeof FeatureFlagIssue.Type;

export class FeatureFlagFileError extends Schema.TaggedErrorClass<FeatureFlagFileError>()(
  "FeatureFlagFileError",
  {
    filePath: Schema.String,
    detail: Schema.String,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  override get message(): string {
    return `Feature flag file error at ${this.filePath}: ${this.detail}`;
  }
}

export class FeatureFlagValueError extends Schema.TaggedErrorClass<FeatureFlagValueError>()(
  "FeatureFlagValueError",
  {
    key: FeatureFlagKey,
    detail: Schema.String,
  },
) {
  override get message(): string {
    return `Feature flag '${this.key}' rejects the value: ${this.detail}`;
  }
}

export const FEATURE_FLAG_DEFAULTS: FeatureFlagSnapshot = Object.freeze(
  Object.fromEntries(FEATURE_FLAGS.map((definition) => [definition.key, definition.default])),
) as FeatureFlagSnapshot;
